import { useContext, useEffect, useMemo, useState } from 'react';
import { FilterContext } from '@/contexts/FilterContext';
import { getSearchModels, type SearchModel } from '@/lib/searchApi';
import type { ParseContext } from '@/lib/search/parseQuery';

/** Everything the query parser needs except `now`, which is taken per keystroke. */
export type SearchParseContext = Omit<ParseContext, 'now' | 'models'> & { models: readonly SearchModel[] };

function userTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * The parse context for the header search (T-148 P4b-1c): the companies the
 * caller can already see (R4: a parse never names an invisible company), the
 * fleet models with their callsigns and room counts, and the user's time zone.
 * Outside a FilterProvider there are no companies, so company names stay text.
 * If the model list fails to load, room and model phrases stay text as well.
 */
export function useSearchParseContext(): SearchParseContext {
  const filterCtx = useContext(FilterContext);
  const companies = filterCtx?.companies;
  const [models, setModels] = useState<readonly SearchModel[]>([]);
  const [timeZone] = useState(userTimeZone);

  useEffect(() => {
    let alive = true;
    getSearchModels()
      .then((m) => { if (alive) setModels(m); })
      .catch(() => { /* models are an enhancement: without them the text search still works */ });
    return () => { alive = false; };
  }, []);

  return useMemo(() => ({
    timeZone,
    companies: (companies ?? []).map((c) => ({ id: c.id, name: c.name, callsigns: c.callsigns ?? [] })),
    models,
  }), [timeZone, companies, models]);
}
